import { EPhoneOS } from '@/models/view/common'
import { isJD, isJDJR } from '@/utils/channel'
import { JDC_ASSETS_URL, JD_ASSETS_URL } from '@/config/common'

import type { LocationQuery } from 'vue-router'

export function browserJump(url: string) {
  window.location.href = url
}

/**
 * @description 跳转页面（京东App内会打开新的webview来加载跳转页面）
 */
export function jump(url: string, openNewWebview = true) {
  if (!url) return
  if (isJD && openNewWebview && !/openapp/i.test(url)) {
    const encodedParam = encodeURIComponent(
      `{"category":"jump","des":"m","url":"${url}"}`
    )
    window.location.href = `openapp.jdmobile://virtual?params=${encodedParam}`
    return
  }

  if (isJDJR && openNewWebview) {
    try {
      JrBridge?.openScheme({
        schemeUrl:
          url.indexOf('?') > -1
            ? `${url}&jrcontainer=h5&jrlogin=false`
            : `${url}?jrcontainer=h5&jrlogin=false`,
      })
    } catch (error) {
      browserJump(url)
    }
    return
  }

  browserJump(url)
}

/**
 * @description 获取当前操作系统：android/iOS，只考虑移动端，鸿蒙系统按android处理
 */
export function getPhoneOS(): EPhoneOS {
  const ua = navigator.userAgent
  const isIOS = !!ua.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/)
  if (isIOS) {
    return EPhoneOS.IOS
  } else {
    return EPhoneOS.Android
  }
}

/**
 * @description 判断是否支持webp格式
 */
const isSupportWebp = function () {
  try {
    return (
      document
        .createElement('canvas')
        .toDataURL('image/webp', 0.5)
        .indexOf('data:image/webp') === 0
    )
  } catch (err) {
    return false
  }
}

/**
 * @param { string } src -图片链接
 * @description 如果支持webp格式获取webp格式图片链接，否则返回原链接
 */
export function getWebPImg(src: string): string {
  const isSupport = isSupportWebp()
  return `${src}${isSupport ? '.webp' : ''}`
}

export function installEruda() {
  if (window.eruda) return 'eruda已经添加了，仔细看下页面右下角有没有~'
  const script = document.createElement('script')
  script.src = '//cdn.jsdelivr.net/npm/eruda'
  script.id = 'erudaIdForDel'
  document.body.appendChild(script)
  script.onload = function () {
    window.eruda?.init()
  }
}

/**
 * @description 零售OSS图片资源加载失败时，切换为为京东云OSS图片资源
 */
export function onImageLoadError(event: Event) {
  const img = event.target as HTMLImageElement
  if (!img.src?.includes(JD_ASSETS_URL)) {
    img.onerror = null
    return
  }
  img.src = img.src?.replace(JD_ASSETS_URL, JDC_ASSETS_URL)
  img.onerror = null
}

/**
 * @description 开发过程中模拟异步场景
 * @param time - 延时时间，单位：毫秒
 */
export async function delay(time = 3000) {
  return new Promise<void>((resolve) => {
    setTimeout(() => {
      resolve()
    }, time)
  })
}

/**
 * @description 用于在显示/关闭弹窗时，禁止/允许页面滚动
 * @param { boolean } ableScroll - 是否允许页面滚动
 * @param { string } selector - 选择器，用于获取页面DOM，默认值为body
 */
export function togglePageScroll(ableScroll: boolean, selector = 'body') {
  const page = document.querySelector(selector) as HTMLElement
  if (!page) return
  if (!ableScroll) {
    document.body.setAttribute('style', 'overflow: hidden; position: fixed')
  } else {
    document.body.setAttribute('style', 'overflow: auto; position: relative')
  }
}

/**
 * @description 获取url中的参数信息
 * @param key - 参数字段名称
 * @param url - URL地址
 */
export function getURLParams(key: string, url?: string) {
  const currentUrl = url || location.href
  const match = RegExp('[?&]' + key + '=([^&|#?]*)').exec(currentUrl)
  return match && decodeURIComponent(match[1].replace(/\+/g, ' '))
}

/**
 * @param { LocationQuery } route - 路由信息，使用时传入route.query或route.params
 * @param { string } field - 字段名称
 * @description 获取路由信息（query/params）中的参数，对于传入多个时（示例：xxx/?a=1&a=2），默认取第一个
 */
export function getRouteParam(
  route: LocationQuery,
  field: string,
  defaultVal = ''
): string {
  const val = route[field]
  if (!val) return defaultVal
  if (Array.isArray(val) && val.length > 0) {
    return val[0] || defaultVal
  }
  return val as string
}

/**
 * @description 预加载图片
 * @param images - 图片链接列表
 */
export async function preLoadImage(images: string[]) {
  return new Promise<void>((resolve) => {
    const imageLength = images.length
    let loadedImageCount = 0
    images.map((item) => {
      const image = new Image()
      image.src = item as string
      image.onload = () => {
        loadedImageCount++
        if (loadedImageCount === imageLength) {
          resolve()
        }
      }
      image.onerror = () => {
        loadedImageCount++
        if (loadedImageCount === imageLength) {
          resolve()
        }
      }
    })
  })
}

/**
 * @description 生成只包含数字和字母的随机字符串
 */
export function generateUniqueId(length: number) {
  const characters =
    'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  let result = ''

  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length)
    result += characters[randomIndex]
  }

  return result
}

/**
 * @description 判断字符串是否为空
 */
export function isNotEmptyString(value: unknown) {
  return typeof value === 'string' && value.length > 0
}
